//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, { useEffect, useRef } from 'react';
import { authService, configService } from '@/services';
import { useAuth } from '@/hooks/useAuth';

interface AppBootstrapProps {
  children: React.ReactNode;
}

// One-time startup orchestration.  Owns no state and provides no context: it
// just runs the bootstrap effect (hydrate auth from the restored session,
// fetch the user profile) by writing into the auth/config/LLM providers above
// it.  app config and config/memory/datasets auto-fetch via
// their own TanStack queries once authed, and the active-dataset selection is
// reconciled reactively in DatasetProvider, so nothing else needs to happen
// here.
const AppBootstrap = ({ children }: AppBootstrapProps) => {
  const {
    setActiveUser, setActiveUserAuthMethod, setUserProfile, markAuthResolved
  } = useAuth();
  // Bootstrap is a one-time hydration, not a reaction to anything: a second
  // run would restore the session and re-read the profile the app already
  // has.  A remount is a new session (the provider tree is keyed on logout),
  // so the guard lives with the mount rather than in a dependency.
  const bootstrapped = useRef(false);

  useEffect(() => {
    // Runs on startup and on browser refresh.  Seeds the connection and user
    // profile data from the restored session.
    const fetchData = async () => {
      // Restore the session FIRST so getUser()/getAuthType() reflect the
      // logged-in user on a browser refresh — they read session state that
      // restoreSession() populates from the auth cookie.  We need the user
      // known up front to gate the profile fetch and to enable the
      // config/datasets queries.  No-ops on the login page: no auth cookie,
      // nothing to restore.
      try {
        await authService.restoreSession();
      } catch (err) {
        console.error('Error restoring session:', err);
      }

      const user = authService.getUser();
      const authType = authService.getAuthType();
      setActiveUserAuthMethod(authType ?? '');
      setActiveUser(typeof user === 'string' ?
                    user :
                    (user ? String(user.token ?? '') : null));
      // The session restore has settled (with or without a user), so the auth
      // gate can stop showing 'pending' and resolve to authenticated/not.  This
      // runs regardless of the config/profile fetches below.
      markAuthResolved();

      // app config, config and datasets auto-fetch via their queries; the
      // active dataset and LLM are computed in their providers from the saved
      // profile + their lists, so setting the profile is all that's needed
      // here.
      if (user) {
        await configService.getUserProfile()
          .then((profile) => setUserProfile(profile))
          // TODO: a LoginRequiredException here should redirect to the login
          //       page, but useNavigate() can't be called from a provider
          //       effect.  Catch it elsewhere and navigate?
          .catch((err) =>
            console.error('AppBootstrap error fetching data:', err));
      }
    };

    if (bootstrapped.current) return;
    bootstrapped.current = true;
    fetchData();
  }, [markAuthResolved, setActiveUser, setActiveUserAuthMethod,
      setUserProfile]);

  return <>{children}</>;
};

export default AppBootstrap;
